import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { setCategories, setCategoriesInfo } from "../../../store/business/businessReducer";

const Categories = ({
    onNext,
    onPrevious,
}: {
    onNext: () => void;
    onPrevious: () => void;
}) => {
    const dispatch = useDispatch();

    // Validation schema
    const validationSchema = Yup.object({
        businessCategory: Yup.string().required("Business category is required"),
        subcategory: Yup.string().required("Subcategory is required"),
        websiteURL: Yup.string()
            .url("Must be a valid URL")
            .required("Website URL is required"),
        socialMediaLinks: Yup.array()
            .of(Yup.string().url("Must be a valid URL"))
            .min(1, "At least one social media link is required"),
    });

    // Formik configuration
    const formik = useFormik({
        initialValues: {
            businessCategory: "",
            subcategory: "",
            otherCategory: "",
            socialMediaLinks: [""],
            websiteURL: "",
            description: "",
        },
        validationSchema,
        onSubmit: (values) => {
            dispatch(setCategories(values)); // Save to Redux
            onNext(); // Go to the next step
        },
    });

    return (
        <form onSubmit={formik.handleSubmit} className="space-y-6">
            <h2 className="text-lg font-bold mb-6">Categories Information</h2>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Business Category */}
                <div>
                    <label
                        htmlFor="businessCategory"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Business Category
                    </label>
                    <input
                        id="businessCategory"
                        type="text"
                        {...formik.getFieldProps("businessCategory")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.businessCategory && formik.errors.businessCategory
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.businessCategory && formik.errors.businessCategory && (
                        <div className="text-red-500 text-sm">
                            {formik.errors.businessCategory}
                        </div>
                    )}
                </div>

                {/* Subcategory */}
                <div>
                    <label
                        htmlFor="subcategory"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Subcategory
                    </label>
                    <input
                        id="subcategory"
                        type="text"
                        {...formik.getFieldProps("subcategory")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.subcategory && formik.errors.subcategory
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.subcategory && formik.errors.subcategory && (
                        <div className="text-red-500 text-sm">
                            {formik.errors.subcategory}
                        </div>
                    )}
                </div>

                {/* Other Category */}
                <div>
                    <label
                        htmlFor="otherCategory"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Other Category
                    </label>
                    <input
                        id="otherCategory"
                        type="text"
                        {...formik.getFieldProps("otherCategory")}
                        className="px-2 mt-1 py-3 block w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                </div>

                {/* Website URL */}
                <div>
                    <label
                        htmlFor="websiteURL"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Website URL
                    </label>
                    <input
                        id="websiteURL"
                        type="text"
                        {...formik.getFieldProps("websiteURL")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.websiteURL && formik.errors.websiteURL
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.websiteURL && formik.errors.websiteURL && (
                        <div className="text-red-500 text-sm">{formik.errors.websiteURL}</div>
                    )}
                </div>

                {/* Social Media Links */}
                <div className="sm:col-span-2">
                    <label
                        htmlFor="socialMediaLinks"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Social Media Links
                    </label>
                    {formik.values.socialMediaLinks.map((link, index) => (
                        <div key={index} className="flex items-center mt-1">
                            <input
                                type="text"
                                value={link}
                                onChange={(e) => {
                                    const updatedLinks = [...formik.values.socialMediaLinks];
                                    updatedLinks[index] = e.target.value;
                                    formik.setFieldValue("socialMediaLinks", updatedLinks);
                                }}
                                className={`px-2 flex-1 py-3 block w-full rounded-md border ${formik.errors.socialMediaLinks &&
                                    formik.errors.socialMediaLinks[index]
                                    ? "border-red-500"
                                    : "border-gray-300"
                                    } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                            />
                            {index > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const updatedLinks = formik.values.socialMediaLinks.filter(
                                            (_, i) => i !== index
                                        );
                                        formik.setFieldValue("socialMediaLinks", updatedLinks);
                                    }}
                                    className="ml-2 text-red-500"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() =>
                            formik.setFieldValue("socialMediaLinks", [
                                ...formik.values.socialMediaLinks,
                                "",
                            ])
                        }
                        className="mt-2 text-green-500"
                    >
                        + Add Social Media Link
                    </button>
                </div>

                {/* Description */}
                <div className="sm:col-span-2">
                    <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Description
                    </label>
                    <textarea
                        id="description"
                        rows={4}
                        {...formik.getFieldProps("description")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.description && formik.errors.description
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-6 flex justify-between">
                <button
                    type="button"
                    onClick={onPrevious}
                    className="px-4 py-2 bg-gray-300 text-gray-600 rounded hover:bg-gray-400"
                >
                    Previous
                </button>
                <button
                    type="submit"
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default Categories;
